import { HTTP_INTERCEPTORS, HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';

import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { IndustryService } from '../services/industry.service';
import { AccountRegistryService } from '../services/account-registry.service';

const TOKEN_HEADER_KEY = 'Authorization';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
    private isLoggingOut = false;
    private industryService: IndustryService | null = null;

    constructor(
        private router: Router,
        private injector: Injector
    ) { }

    /**
     * Get IndustryService using lazy injection to avoid circular dependency
     */
    private getIndustryService(): IndustryService {
        if (!this.industryService) {
            this.industryService = this.injector.get(IndustryService);
        }
        return this.industryService;
    }

    /**
     * Check if the URL should be transformed based on industry
     * Excludes auth endpoints from transformation
     */
    private shouldTransformUrl(url: string): boolean {
        // Don't transform auth endpoints
        if (url.includes('/auth/')) {
            return false;
        }

        // Don't transform URLs that already have industry path segment
        if (url.includes('/mobile/') || url.includes('/kot/')) {
            return false;
        }

        // Transform all other API endpoints
        return true;
    }

    /**
     * Transform URL based on current industry configuration
     */
    private transformUrl(originalUrl: string): string {
        if (!this.shouldTransformUrl(originalUrl)) {
            return originalUrl;
        }

        try {
            const industryService = this.getIndustryService();
            const url = new URL(originalUrl);

            // Transform the pathname
            const transformedPath = industryService.getApiPath(url.pathname);

            // Reconstruct the URL with transformed path
            url.pathname = transformedPath;

            return url.toString();
        } catch (error) {
            // If URL parsing fails, return original
            console.warn('Failed to transform URL:', originalUrl, error);
            return originalUrl;
        }
    }

    intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        let authreq = request;

        const hasExistingAuth = request.headers.has(TOKEN_HEADER_KEY);

        if (!hasExistingAuth) {
            const token = localStorage.getItem('name');
            if (token != null) {
                let userInfo = JSON.parse(token || '{}');
                authreq = request.clone({ headers: request.headers.set(TOKEN_HEADER_KEY, 'Bearer ' + userInfo['accessToken']) });
            }
        }

        // Transform URL based on industry configuration
        const transformedUrl = this.transformUrl(authreq.url);
        if (transformedUrl !== authreq.url) {
            console.log(`[Industry Interceptor] URL transformed: ${authreq.url} -> ${transformedUrl}`);
            authreq = authreq.clone({ url: transformedUrl });
        }

        return next.handle(authreq).pipe(
            catchError((error: HttpErrorResponse) => {
                if (error.status === 401 && !this.isLoggingOut) {
                    const wasLoggedIn = localStorage.getItem('name') !== null;

                    if (wasLoggedIn) {
                        this.isLoggingOut = true;
                        this.injector.get(AccountRegistryService).markActiveExpired();

                        localStorage.removeItem('name');
                        localStorage.removeItem('client_id');
                        localStorage.removeItem('auth_credentials');
                        localStorage.removeItem('storename');
                        localStorage.removeItem('auth_token');
                        localStorage.removeItem('user_data');
                        localStorage.removeItem('punchStatus');
                        localStorage.removeItem('shiftStartTime');
                        localStorage.removeItem('shiftEndTime');

                        this.router.navigate(['/dashboard']).then(() => {
                            window.location.reload();
                        });
                    }
                }
                return throwError(() => error);
            })
        );
    }
}

export const authInterceptorProviders = [{ provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true }];
